import { Injectable } from '@angular/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { AlertController, NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getMessaging, onMessage } from 'firebase/messaging';
import { firstValueFrom, Observable } from 'rxjs';
import { debounceTime, distinctUntilKeyChanged, filter, map, startWith, switchMap, take, tap } from 'rxjs/operators';

import { DataEjecucion } from '../../../private/shared/models/menu-item';
import { ClearGetEjecucionTarea, getEjecucionTarea, getNotifications, setEjecucion } from '../../../store/actions';
import { AppState } from '../../../store/app-reducers';
import { ResulExePushPage } from '../../modals/result-exe-push/result-exe-push.page';
import {
  IFormatDataPush,
  IPushAndroidFirebase,
  IPushAndroidFirebaseBackGround,
  IPushAndroidFirebaseForeGround,
  IPushIOSFirebase,
  IPushIOSFirebaseBackground,
  IPushIOSFirebaseForeground,
  IPushWebFirebase
} from '../../models/PushNotifications.model.model';
import { ModalsService } from '../modals/modals.service';
import { SharedServiceService } from '../shared-service.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {

  private alertNoti: HTMLIonAlertElement = {} as HTMLIonAlertElement;
  private readonly requiredUtmKeys = [
    'utm_campaign',
    'utm_content',
    'utm_medium',
    'utm_source',
    'utm_term',
  ] as const;

  constructor(
    private sharedS: SharedServiceService
    , private translate: TranslateService
    , private modalsS: ModalsService
    , private nav: NavController
    , private alertCtrl: AlertController
    , private store: Store<AppState>
  ) { }

  /** 
   * Devuelve un Observable que escucha los mensajes entrantes en primer plano
   * Utiliza diferentes implementaciones según la plataforma:
   * - En dispositivos nativos (iOS/Android): usa los listeners de @capacitor-firebase/messaging
   * - En web: usa onMessage de la biblioteca Firebase Web v9
   */
  public onMessageReceived = () => {
    // Para plataformas nativas (iOS, Android)
    if (Capacitor.isNativePlatform()) {
      return new Observable(observer => {
        // Configurar listener para mensajes en primer plano
        FirebaseMessaging.addListener('notificationReceived', (message) => {
          observer.next(message);
        });

        // Configurar listener para cuando se hace click en la notificación
        FirebaseMessaging.addListener('notificationActionPerformed', (action) => {
          observer.next({
            ...action.notification,
            tap: true
          });
        });

        return () => {
          // Limpiar listeners al destruir el observable
          FirebaseMessaging.removeAllListeners();
        };
      });
    }
    // Para plataforma web
    else {
      return new Observable(observer => {
        try {
          const messaging = getMessaging();

          // Configurar el listener web
          const unsubscribe = onMessage(messaging, (payload) => {

            // Adaptar el formato del payload web al formato común que espera la app
            const adaptedPayload = {
              notification: {
                data: payload.data || {},
                title: payload.notification?.title || '',
                body: payload.notification?.body || '',
                id: payload.messageId || Date.now().toString()
              },
              data: payload.data || {}
            };

            observer.next(adaptedPayload);
          });

          return () => {
            // Limpiar listener cuando se destruye el observable
            if (unsubscribe) unsubscribe();
          };
        } catch (error) {
          return () => { };
        }
      });
    }
  };

  /**
   * Inicializa la suscripción para recibir notificaciones
   * Maneja ambos entornos: nativo (iOS/Android) y web
   */
  public initForegroundNotifications = async () => {
    const state$ = this.sharedS.conexionState.pipe(debounceTime(300), filter(state => state));
    try {
      const isNative = Capacitor.isNativePlatform();
      let hasPermission = false;

      if (isNative) {
        const permissionStatus = await FirebaseMessaging.requestPermissions();
        hasPermission = permissionStatus.receive === 'granted';
      } else {
        if (Notification.permission === 'granted') {
          hasPermission = true;
        } else if (Notification.permission !== 'denied') {
          const permission = await Notification.requestPermission();
          hasPermission = permission === 'granted';
        }
      }

      if (hasPermission) {
        const deviceInfo = await Device.getInfo();
        const isIOS = deviceInfo.platform === 'ios';

        this.onMessageReceived()
          .pipe(
            startWith((mns: any) => mns),
            switchMap(message => state$.pipe(map(() => message))),
            tap(() => this.store.dispatch(getNotifications())),
            filter(message => typeof message === 'object'),
            map(message => {
              if (isNative) {
                return isIOS ?
                  this.formatPushIos(message as IPushIOSFirebase) :
                  this.formatPushAndroid(message as IPushAndroidFirebase);
              } else {
                return this.formatPushWeb(message as IPushWebFirebase);
              }
            }),
            debounceTime(1500),
            distinctUntilKeyChanged('id'),
            filter(message => (message.data && typeof message.data === 'object' && 'Ruta' in message.data) && message.hasOwnProperty('body') || message.hasOwnProperty('title'))
          ).subscribe(message => {
            isNative ? this.callbackMobile(message) : this.callbackWeb(message);
          });
      }
    } catch (error) {
      return;
    }
  }

  /**
   * Pasa el title y body a raiz del objecto para reultilizar el callback
   * Adaptado para @capacitor-firebase/messaging
   */
  private formatPushIos = (push: IPushIOSFirebase): IFormatDataPush => {
    if ('notification' in push) {
      const pushBackground = push as IPushIOSFirebaseForeground;
      return {
        ...pushBackground.notification.data,
        title: pushBackground.notification.data.aps.alert.title,
        body: pushBackground.notification.data.aps.alert.body,
        id: pushBackground.notification.data['gcm.message_id']
      }
    } else {
      const pushForeground = push as IPushIOSFirebaseBackground;
      return {
        ...pushForeground.data,
        title: pushForeground.data.aps.alert.title,
        body: pushForeground.data.aps.alert.body,
        id: pushForeground.data['gcm.message_id'],
        tap: pushForeground.tap || false
      }
    }
  }

  /**
   * Pasa el title y body a raiz del objecto para reultilizar el callback
   * Adaptado para @capacitor-firebase/messaging
   */
  private formatPushAndroid = (push: IPushAndroidFirebase): IFormatDataPush => {
    if ('notification' in push) {
      const pushBack = push as IPushAndroidFirebaseBackGround;
      return {
        ...(pushBack.notification.hasOwnProperty('data') ? pushBack.notification.data : {}),
        id: pushBack.notification.id,
        title: pushBack.notification.title,
        body: pushBack.notification.body
      }
    } else {
      const pushFore: IPushAndroidFirebaseForeGround = push as IPushAndroidFirebaseForeGround;
      return {
        ...(pushFore.hasOwnProperty('data') ? pushFore.data : {}),
        id: pushFore.id,
        tap: pushFore.tap || false,
        title: (pushFore.hasOwnProperty('data') ? pushFore.data?.notification_title || '' : ''),
        body: (pushFore.hasOwnProperty('data') ? pushFore.data?.notification_body || '' : '')
      }
    }
  };

  /**
   * Formatea los mensajes recibidos en entorno web
   * Adaptado para Firebase Web v9
   */
  private formatPushWeb = (push: IPushWebFirebase): IFormatDataPush => ({
    ...push.data,
    id: push.notification.id,
    title: push.notification.title,
    body: push.notification.body
  });

  /**
   * Añade info de fechas de ejecución actualizadas
   */
  private callbackPushEjecucion = async (mns: IFormatDataPush) => {
    if (Number(mns.id_modulo) === 8) {
      this.store.dispatch(ClearGetEjecucionTarea());
      setTimeout(() => this.store.dispatch(getEjecucionTarea()), 500);

      let dataEjecucion: DataEjecucion = await firstValueFrom(this.store.select('userData').pipe(map(value => value.Ejecucion), take(1)));
      const FechasEjecucion = [{
        HorarioEjecucion: mns.horarioEjecucion
        , FechaEjecucionFin: mns.fechaEjecucionFin
        , Check: mns.check === 'true'
      }];

      if ((dataEjecucion.FechasEjecucion || []).length > 0 && (dataEjecucion.FechasEjecucion || []).some(f => f.HorarioEjecucion !== FechasEjecucion[0].HorarioEjecucion)) {
        dataEjecucion = { ...dataEjecucion, FechasEjecucion };
        this.store.dispatch(setEjecucion(dataEjecucion));
      } else {
        dataEjecucion = { ...dataEjecucion, FechasEjecucion };
        this.store.dispatch(setEjecucion(dataEjecucion));
      }
    }
  }

  /**
   * Callback de la push de Android
   */
  private callbackMobile = async (push: IFormatDataPush) => {
    const queryParams = this.requiredUtmKeys.filter(key => !push[key]).length > 0 ? {} : {
      utm_source: push.utm_source,
      utm_medium: push.utm_medium,
      utm_campaign: push.utm_campaign,
      utm_content: push.utm_content,
      utm_term: push.utm_term,
    };

    if (push.hasOwnProperty('tap') && push.tap) {
      this.nav.navigateForward(`/${push.click_action}`, { animated: true, animationDirection: 'forward', queryParams });
    } else {
      await this.notisDismiss();

      let confirm = false;

      switch (Number(push.id_modulo)) {
        case 8:
          this.callbackPushEjecucion(push)
          confirm = await this.modalsS.modalEjecucionPush(push);
          if (confirm) {
            this.nav.navigateForward(['/main/Autoevaluacion'], { animated: true, animationDirection: 'forward', queryParams });
          }
          break;
        case 18:
          confirm = await this.modalsS.modalEjecucionResultPush(ResulExePushPage, push.title || '', push.body || '');
          if (confirm) { this.nav.navigateForward([`/${push.click_action}`], { animated: true, animationDirection: 'forward', queryParams }); }
          break;
        default:
          confirm = await this.openAlertNotification(push.title || '', push.body || '');
          if (confirm) { this.nav.navigateForward([`/${push.click_action}`], { animated: true, animationDirection: 'forward', queryParams }); }
          break;
      }

    }
  }

  /**
   * Callback para manejar notificaciones en entorno web
   * Procesa los mensajes formateados para mostrar notificaciones y navegar
   */
  private callbackWeb = async (push: IFormatDataPush) => {
    await this.notisDismiss();
    let confirm = false;
    const queryParams = this.requiredUtmKeys.filter(key => !push[key]).length > 0 ? {} : {
      utm_source: push.utm_source,
      utm_medium: push.utm_medium,
      utm_campaign: push.utm_campaign,
      utm_content: push.utm_content,
      utm_term: push.utm_term,
    };

    if (push.hasOwnProperty('tap') && push.tap) {
      this.nav.navigateForward([`/${push.click_action}`], { animated: true, animationDirection: 'forward', queryParams });
      return;
    }

    const id_modulo = Number(push.id_modulo || 0);

    switch (id_modulo) {
      case 8:
        this.callbackPushEjecucion(push);
        confirm = await this.modalsS.modalEjecucionPush(push);
        if (confirm) {
          this.nav.navigateForward(['/main/Autoevaluacion'], { animated: true, animationDirection: 'forward', queryParams });
        }
        break;
      case 18:
        confirm = await this.modalsS.modalEjecucionResultPush(ResulExePushPage, push.title || '', push.body || '');
        if (confirm) {
          this.nav.navigateForward([`/${push.click_action}`], { animated: true, animationDirection: 'forward', queryParams });
        }
        break;
      default:
        confirm = await this.openAlertNotification(push.title || '', push.body || '');
        if (confirm && push.click_action) {
          this.nav.navigateForward([`/${push.click_action}`], { animated: true, animationDirection: 'forward', queryParams });
        }
        break;
    }
  }

  /**
   * Notificación generica al recibir push en primer plano
   */
  private openAlertNotification = async (subHeader: string, message: string) => {
    if (!await this.modalsS.modalActives()) {
      const header = this.translate.instant('Titles.notification');
      const button = this.translate.instant('Buttons.accept');


      this.alertNoti = await this.alertCtrl.create({
        animated: true,
        backdropDismiss: true,
        keyboardClose: true,
        translucent: true,
        mode: 'ios',
        cssClass: 'alert-notification',
        header,
        subHeader,
        message,
        buttons: [
          {
            text: button,
            handler: () => true
          }
        ]
      });

      await this.alertNoti.present();

      const { data } = await this.alertNoti.onDidDismiss();
      return data;
    }
    return false;
  }

  /**
   * Para cerrar todas las alertas o modales activas y no hagan ruido cual es la principal
   */
  private notisDismiss = async () => {
    if (Object.keys(this.alertNoti).length > 0) {
      await this.alertNoti.dismiss(false);
    }
    await this.modalsS.modalDismiss();
  }
}
